import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../i18n";
import { fetchRegions } from "../lib/api";
import { ArrowUpRight, Compass, Coins, Route, CloudSun, MessageSquareText, MessagesSquare } from "lucide-react";
import SEO from "../components/SEO";
import WowJourneyBand from "../components/WowJourneyBand";
import { trackEvent } from "../lib/analytics";

const HERO_IMG = "https://images.pexels.com/photos/13713831/pexels-photo-13713831.jpeg";

// Bento sizes for 9 travel worlds: 12-col grid, 4 rows, transylvania is the hero (2-row span)
const BENTO_SPAN = {
  transylvania: "md:col-span-8 md:row-span-2",
  bucovina:     "md:col-span-4",
  muntenia:     "md:col-span-4",
  maramures:    "md:col-span-4",
  oltenia:      "md:col-span-4",
  banat:        "md:col-span-4",
  moldova:      "md:col-span-4",
  dobrogea:     "md:col-span-4",
  crisana:      "md:col-span-4",
};

const TOOL_ICONS = {
  currency: Coins, distance: Route, budget: Compass, weather: CloudSun, phrasebook: MessageSquareText,
};

export default function Home() {
  const { locale, t } = useLocale();
  const [regions, setRegions] = useState([]);

  useEffect(() => { fetchRegions().then(setRegions).catch(() => {}); }, []);

  const nameKey = locale === "ro" ? "name_ro" : "name_en";
  const taglineKey = locale === "ro" ? "tagline_ro" : "tagline_en";

  const heroLines = t("hero.title");

  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "WowRomania",
    url: "https://wowromania.com",
    inLanguage: [locale === "ro" ? "ro" : "en"],
    description: t("hero.sub"),
    potentialAction: {
      "@type": "SearchAction",
      target: "https://wowromania.com/concierge?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <div>
      <SEO
        exactTitle="WOWROMANIA | Discover the Best of Romania"
        description="Discover Romania’s most beautiful destinations, authentic experiences, hidden gems, hotels, restaurants, and local businesses with WOWROMANIA."
        path="/"
        jsonLd={[websiteLd]}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt={locale === "ro" ? "Munții Carpați învăluiți în ceață la răsărit" : "Foggy Carpathian mountains at dawn"}
            className="w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16 min-h-[92vh] flex flex-col justify-end">
          <p className="font-mono-wr text-[13px] sm:text-[14px] uppercase tracking-[0.3em] text-white/85 mb-6 wr-fade wr-fade-1">
            /  {t("hero.kicker")}
          </p>
          <h1 className="font-editorial text-white text-[52px] sm:text-[76px] lg:text-[104px] leading-[0.95] tracking-tight max-w-6xl">
            {heroLines.map((line, i) => (
              <span key={i} className={`block wr-fade wr-fade-${i + 2}`}>
                {i === heroLines.length - 1 ? (
                  <em className="not-italic border-b-[3px] border-[var(--wr-red)] pb-1">{line}</em>
                ) : line}
              </span>
            ))}
          </h1>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5 wr-fade wr-fade-5">
            <Link
              to="/concierge"
              data-testid="hero-cta-concierge"
              className="group inline-flex items-center gap-3 px-6 py-4 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-red-hover)] transition-colors duration-300 rounded-sm"
            >
              <MessagesSquare size={18} />
              <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">{t("hero.cta_primary")}</span>
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/tools"
              data-testid="hero-cta-tools"
              className="inline-flex items-center gap-3 px-6 py-4 border border-white/40 text-white hover:bg-white hover:text-[var(--wr-ink)] transition-colors duration-300 rounded-sm"
            >
              <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">{t("hero.cta_secondary")}</span>
            </Link>
          </div>
          <p className="mt-10 text-white/80 max-w-xl text-[15px] leading-relaxed wr-fade wr-fade-6">
            {t("hero.sub")}
          </p>
        </div>
      </section>

      {/* WOW JOURNEY — introduction band (gated by REACT_APP_WOW_JOURNEY_ENABLED) */}
      <WowJourneyBand />

      {/* REGIONS BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="regions-section">
        <div className="mb-16 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">
              / {locale === "ro" ? "LUMI" : "WORLDS"} · 09
            </p>
            <h2 className="font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[var(--wr-ink)]">
              {t("regions.heading")}
            </h2>
          </div>
          <p className="lg:col-span-4 text-[16px] leading-relaxed text-[var(--wr-ink-2)] max-w-md">
            {t("regions.sub")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[220px] md:auto-rows-[240px] gap-4">
          {regions.map((r, i) => (
            <Link
              key={r.slug}
              to={`/regions/${r.slug}`}
              data-testid={`region-card-${r.slug}`}
              className={`relative wr-img-mask group border border-[var(--wr-border)] hover:border-[var(--wr-ink)] transition-colors duration-500 ${BENTO_SPAN[r.slug] || "md:col-span-4"}`}
            >
              <img src={r.image} alt={r[nameKey]} className="w-full h-full object-cover" style={{ objectPosition: r.image_position }} loading="lazy" decoding="async" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-between text-white">
                <div className="flex items-center justify-between">
                  <p className="font-mono-wr text-[10px] uppercase tracking-widest text-white/85">
                    / {String(i + 1).padStart(2, "0")}
                  </p>
                  <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div>
                  <h3 className="font-editorial text-3xl sm:text-4xl leading-none tracking-tight">
                    {r[nameKey]}
                  </h3>
                  <p className="mt-2 text-[13px] text-white/85 max-w-md leading-snug">
                    {r[taglineKey]}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TOOLS STRIP */}
      <section className="border-y border-[var(--wr-border)] bg-[var(--wr-surface-2)]" data-testid="tools-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="mb-16 grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">
                / {locale === "ro" ? "TOOLS" : "TOOLS"} · 05
              </p>
              <h2 className="font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[var(--wr-ink)]">
                {t("tools.heading")}
              </h2>
            </div>
            <p className="lg:col-span-4 text-[16px] leading-relaxed text-[var(--wr-ink-2)] max-w-md">
              {t("tools.sub")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {["currency", "distance", "budget", "weather", "phrasebook"].map((slug, i) => {
              const Icon = TOOL_ICONS[slug];
              return (
                <Link
                  key={slug}
                  to={`/tools/${slug}`}
                  data-testid={`tool-card-${slug}`}
                  className="wr-sharp-card p-6 group flex flex-col justify-between min-h-[220px] hover:-translate-y-1"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={22} className="text-[var(--wr-red)]" />
                    <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">
                      / 0{i + 1}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-editorial text-3xl leading-none tracking-tight text-[var(--wr-ink)] mb-2">
                      {t(`tools.${slug}`)}
                    </h3>
                    <p className="text-[13px] text-[var(--wr-ink-2)] leading-snug">{t(`tools.${slug}_sub`)}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono-wr uppercase tracking-widest text-[var(--wr-ink)] group-hover:text-[var(--wr-red)]">
                      {t("tools.open")}
                      <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONCIERGE CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12" data-testid="concierge-cta-section">
        <div className="lg:col-span-7">
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">
            / {locale === "ro" ? "CONCIERGE" : "CONCIERGE"}
          </p>
          <h2 className="font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[var(--wr-ink)] max-w-3xl">
            {t("concierge.heading")}
          </h2>
          <p className="mt-6 text-[17px] text-[var(--wr-ink-2)] leading-relaxed max-w-xl">
            {t("concierge.sub")}
          </p>
          <Link
            to="/concierge"
            data-testid="concierge-cta"
            className="mt-10 inline-flex items-center gap-3 px-6 py-4 bg-[var(--wr-ink)] text-[var(--wr-bg)] hover:bg-[var(--wr-red)] transition-colors duration-300 rounded-sm"
          >
            <MessagesSquare size={18} />
            <span className="font-mono-wr text-[12px] uppercase tracking-[0.2em]">{t("concierge.open")}</span>
            <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="lg:col-span-5 relative border border-[var(--wr-border)] p-6 bg-[var(--wr-night)] text-[var(--wr-bg)] rounded-sm">
          <p className="font-mono-wr text-[9px] uppercase tracking-widest text-white/40 mb-4">SAMPLE CONVERSATION</p>
          <div className="space-y-3 text-[14px] leading-relaxed">
            <p className="text-white/50">— {locale === "ro" ? "Ai 4 zile, prima dată în România. Ce faci?" : "You have 4 days, first time in Romania. What do you do?"}</p>
            <p className="text-white">Land Bucharest. Overnight, then train to Brașov at dawn. Two nights in the Saxon triangle — Bran, Râșnov, Sighișoara. Return via Sinaia and Peleș. Skip Dracula tourist traps; eat at Casa Georgius Krauss.</p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 flex flex-col items-center" data-testid="promo-creditcardtm">
        <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-4" data-testid="promo-label">
          {locale === "ro" ? "Publicitate" : "Advertisement"}
        </p>
        <a
          href="https://creditcardtm.com/calculators/car-loan"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="promo-creditcardtm-link"
          onClick={() => trackEvent("promo_click", { promo_id: "creditcardtm_car_loan", link_domain: "creditcardtm.com" })}
          className="block w-full max-w-[400px]"
        >
          <img
            src="/promo/creditcardtm-car-loan.webp"
            width="800"
            height="1000"
            loading="lazy"
            decoding="async"
            alt={locale === "ro"
              ? "Reclamă CreditCardTM: Cumperi o mașină? Calculator gratuit de credit auto pentru a compara ratele lunare și dobânda totală pe 60, 72 și 84 de luni."
              : "CreditCardTM ad: Buying a car? Free car loan calculator to compare monthly payments and total interest over 60, 72 and 84 months."}
            className="block w-full h-auto"
          />
        </a>
      </section>
    </div>
  );
}
